import * as React from 'react'

import { Box, Button, Icon, Text, useClassNames } from '@factorialco/deprecated-design-system'
import { useStripe } from '@stripe/react-stripe-js'
import { CardDetailFragment } from 'generated/resources/graphql'

import FactorialLogo from 'modules/core/components/Logo/Factorial'
import VISALogo from 'modules/core/components/Logo/VISA'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { cardLogoColor } from 'modules/banking/lib/cards/cardLogoColor'
import { isActive } from 'modules/banking/lib/cards/isActive'
import { isPending } from 'modules/banking/lib/cards/isPending'
import { isRequested } from 'modules/banking/lib/cards/isRequested'
import { isSingleUse } from 'modules/banking/lib/cards/isSingleUse'
import { isVirtual } from 'modules/banking/lib/cards/isVirtual'
import { last4digits } from 'modules/banking/lib/cards/last4digits'
import useCardDetails, { isCardDetails } from 'modules/expenses/hooks/useCardDetails'

import './index.styles.css'
import { cardNumberStyle, copyButtonStyle, customFont, cvvStyle } from './styles'

const stylesRefs = {
  withCustomFont: '_CardImage_withCustomFont',
  cardNumber: '_CardImage_cardNumber',
}

interface Props {
  card: CardDetailFragment
  canSeeCardDetails: boolean
}

export const CardImage = ({ card, canSeeCardDetails }: Props) => {
  const cx = useClassNames(stylesRefs)
  const cvvRef = React.useRef(null)
  const copyCardNumberRef = React.useRef(null)
  const cardNumberRef = React.useRef(null)
  const stripe = useStripe()
  const { loadCardDetails, cardDetails, loadingCardDetails } = useCardDetails(
    card.externalId,
    card.id + '',
    ['cvc', 'number']
  )
  const [detailsVisible, setDetailsVisible] = React.useState(false)
  const toggleDetailsVisible = () => {
    setDetailsVisible(!detailsVisible)
    // track(
    //   trackingEvents.expenses.goToSeeCardNumbers,
    //   card.amplitudeData({ context: 'personal' })
    // )
  }

  const showStripeError = () => {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('expenses_v2.cards.details.error_loading_card'),
    })
  }

  const elements = stripe?.elements({
    fonts: [customFont],
  })

  useEffect(() => {
    if (detailsVisible && !isCardDetails(cardDetails)) loadCardDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, detailsVisible])

  useEffect(() => {
    if (detailsVisible && isCardDetails(cardDetails)) {
      // The types exported by the stripe sdk are incorrect, so placing this until they update the sdk
      // @ts-expect-error:next-line
      const cvvElement = elements?.create('issuingCardCvcDisplay', {
        style: cvvStyle(card.type),
        issuingCard: card.externalId,
      })

      // @ts-expect-error:next-line
      const cardNumberElement = elements?.create('issuingCardNumberDisplay', {
        style: cardNumberStyle(card.type),
        issuingCard: card.externalId,
      })

      // @ts-expect-error:next-line
      const copyCardNumberElement = elements?.create('issuingCardCopyButton', {
        toCopy: 'number',
        style: copyButtonStyle,
        issuingCard: card.externalId,
      })

      copyCardNumberElement?.on('click', () => {
        app.setFlash({
          type: 'correct',
          message: i18n.t('expenses_v2.cards.details.success_copy_number'),
        })
      })

      if (cardNumberRef.current) {
        cardNumberElement?.mount(cardNumberRef.current)
      }

      if (cvvRef.current) {
        cvvElement?.mount(cvvRef.current)
      }

      if (copyCardNumberRef.current) {
        copyCardNumberElement?.mount(copyCardNumberRef.current)
      }

      cardNumberElement?.on('loaderror', showStripeError)
      cvvElement?.on('loaderror', showStripeError)
      copyCardNumberElement?.on('loaderror', showStripeError)
    }
  }, [isCardDetails(cardDetails), detailsVisible, card.externalId])

  const expirationDate =
    isRequested(card) || isPending(card) ? '•••' : `${card.expMonth}/${card.expYear}`

  const textColor = cardLogoColor(card)

  const CardNumber = () => {
    const last4 = last4digits(card)

    const cardNumber = [
      [0, '••••'],
      [1, '••••'],
      [2, '••••'],
      [3, last4],
    ]

    return (
      <Box minHeight="s40" flexDirection="row" alignItems="center" gap="s2">
        <Box flexDirection="row">
          <Box
            flexDirection={detailsVisible ? 'column' : 'row'}
            gap="s4"
            height="s24"
            style={{ minWidth: detailsVisible ? '218px' : undefined }}
            ref={cardNumberRef}
          >
            {!detailsVisible &&
              cardNumber.map((element) => {
                return (
                  <Text.Normal color={textColor} inline size="300" key={element[0]}>
                    <Box component="span" inline className={cx('withCustomFont', 'cardNumber')}>
                      {element[1]}
                    </Box>
                  </Text.Normal>
                )
              })}
            {loadingCardDetails && (
              <Text.Normal color={textColor} inline size="300" weight="semibold">
                {i18n.t('banking.cards.loading_card_number')}
              </Text.Normal>
            )}
          </Box>
        </Box>
        <>
          <Icon.Medium icon="contactless" color={textColor} />
        </>
        {detailsVisible && (
          <Box alignItems="flexEnd" style={{ marginLeft: 'auto' }}>
            <Box
              background={textColor}
              alignItems="center"
              justifyContent="center"
              padding="s2"
              borderRadius={{ all: 'rel500' }}
              height="s40"
              width="s40"
              boxShadow="s400"
              className={'_CardImage_copyButtonContainer'}
            >
              <Button.Decorative iconName="duplicate" />
            </Box>
            {/* Hidden Stripe button positioned on top */}
            <Box
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
              }}
              ref={copyCardNumberRef}
            />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <>
      <Box
        boxShadow="s200"
        style={{
          position: 'relative',
          paddingLeft: '18px',
          paddingRight: '18px',
          borderRadius: '12px',
        }}
        paddingY="s16"
      >
        <Box padding="s2" gap="s6">
          {canSeeCardDetails && (isVirtual(card) || (isSingleUse(card) && isActive(card))) && (
            <Box
              alignItems="center"
              justifyContent="center"
              padding="s2"
              width="s40"
              height="s40"
              borderRadius={{ all: 'rel500' }}
              background={textColor}
              boxShadow="s400"
              className={'_CardImage_eyeButtonContainer'}
            >
              <Button
                onClick={toggleDetailsVisible}
                iconName={detailsVisible ? 'eye-invisible' : 'eye-visible'}
              />
            </Box>
          )}
          <Box
            flexDirection="row"
            paddingBottom="s12"
            alignItems="center"
            justifyContent="spaceBetween"
          >
            <Text.Normal color={textColor}>
              <FactorialLogo useCurrentColor width={110} height={24} />
            </Text.Normal>
            <Text.Normal color={textColor} size="100">
              <Box component="span" className={'_CardImage_cardType'}>
                {isVirtual(card) && i18n.t('expenses_v2.cards.type.short_virtual')}
                {isSingleUse(card) && i18n.t('expenses_v2.cards.type.short_single_use')}
              </Box>
            </Text.Normal>
          </Box>

          <Box flexDirection="row">
            <Box
              paddingX="s8"
              paddingY="s2"
              background={!isSingleUse(card) ? 'darker100' : 'grey600'}
              borderRadius={{ all: 'abs008' }}
            >
              <Text size="300" color={textColor} weight="semibold">
                {card.alias || i18n.t('expenses_v2.cards.details.factorial_card')}{' '}
              </Text>
            </Box>
          </Box>
          <CardNumber />
          <Text.Normal color={textColor}>
            <Box component="span" className={'_CardImage_withCustomFont'}>
              {card.fullName}
            </Box>
          </Text.Normal>
          <Box flexDirection="row" gap="s32">
            <Box flexDirection="column">
              <Text.Normal color={textColor}>
                <Box component="span" className={'_CardImage_cvv'}>
                  {i18n.t('expenses_v2.cards.details.valid_thru')}
                </Box>
              </Text.Normal>
              <Text.Normal color={textColor}>
                <Box component="span" className={'_CardImage_withCustomFont'}>
                  {expirationDate}
                </Box>
              </Text.Normal>
            </Box>
            <Box flexDirection="column">
              <Text.Normal color={textColor}>
                <Box component="span" className={'_CardImage_cvv'}>
                  {i18n.t('expenses_v2.cards.details.cvv')}
                </Box>
              </Text.Normal>
              <>
                {detailsVisible ? (
                  <Box
                    justifyContent="center"
                    ref={cvvRef}
                    minWidth="s40"
                    minHeight="s24"
                    className={'_CardImage_withCustomFont'}
                  />
                ) : (
                  <Text.Normal color={textColor}>
                    <Box component="span" className={'_CardImage_withCustomFont'}>
                      •••
                    </Box>
                  </Text.Normal>
                )}
              </>
            </Box>
            <Box
              alignItems="flexEnd"
              justifyContent="center"
              flexDirection="row"
              style={{ marginLeft: 'auto' }}
            >
              <Text.Normal color={textColor}>
                <VISALogo />
              </Text.Normal>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
